import * as React from 'react';
import { useState } from 'react';
import { CSSTransitionGroup } from 'react-transition-group-v1';

import keysToConstants from 'js/lib/keysToConstants';

import type { ApiError } from 'bundles/enroll-common/utils/errorUtils';
import { useEnrollInS12nSubscription } from 'bundles/enroll-common/utils/useEnrollInS12nSubscription';
import EnrollErrorModal from 'bundles/enroll-modal/components/common/EnrollErrorModal';
import MixAndMatchS12nContextMessage from 'bundles/enroll-modal/components/common/MixAndMatchS12nContextMessage';
import PromotionApplicableCheckoutMessage from 'bundles/enroll-modal/components/common/PromotionApplicableCheckoutMessage';
import S12nSubscriptionEnrollButton from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionEnrollButton';
import S12nSubscriptionFooter from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionFooter';
import S12nSubscriptionPriceHeader from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionPriceHeader';
import S12nSubscriptionVProp from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionVProp';
import S12nSubscriptionVPropFreeTrial from 'bundles/enroll-modal/components/s12n-subscriptions/free-trialV2/S12nSubscriptionVPropFreeTrial';
import usePageData from 'bundles/enroll-modal/data/usePageData';
import { useEnrollModalEventing } from 'bundles/enroll-modal/utils/eventingUtils';
import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/enroll';

import 'css!./__styles__/SubscriptionEnrollModal';

const MODAL_TYPES = keysToConstants(['ENROLL', 'ERROR']);

type PropsFromCaller = {
  onClose: () => void;
  onSdp: boolean;
  isFromS12nSelection?: boolean;
  didSkipMixAndMatchModal?: boolean;
};

const S12nSubscriptionEnrollModal = ({
  onClose,
  onSdp,
  isFromS12nSelection = false,
  didSkipMixAndMatchModal = false,
}: PropsFromCaller) => {
  const [activeModal, setActiveModal] = useState<keyof typeof MODAL_TYPES | null>(MODAL_TYPES.ENROLL);
  const [error, setError] = useState<ApiError | undefined>();
  const [enrolling, setEnrolling] = useState(false);
  const { getEnrollModalTrackingRef, trackEnrollModalClose } = useEnrollModalEventing();
  const visibilityTrackingRef = getEnrollModalTrackingRef();
  const { s12n, course, isSpecialization } = usePageData();
  const {
    s12nMonthlySkuId,
    canEnrollThroughS12nSubscription,
    canEnrollThroughS12nSubscriptionFreeTrial,
    enrollInSubscription,
  } = useEnrollInS12nSubscription(isFromS12nSelection);

  if (!s12n || !s12nMonthlySkuId) {
    return null;
  }

  const handleClose = () => {
    trackEnrollModalClose();
    onClose();
  };

  const handleError = (apiError: ApiError) => {
    setEnrolling(false);
    setActiveModal(MODAL_TYPES.ERROR);
    setError(apiError);
  };

  const onClickSubscribeCta = () => {
    setEnrolling(true);
    enrollInSubscription().catch(handleError);
  };

  const renderHeader = (): React.ReactNode => {
    if (canEnrollThroughS12nSubscription) {
      return <S12nSubscriptionPriceHeader />;
    } else {
      return null;
    }
  };

  const renderBody = (): React.ReactNode => {
    if (canEnrollThroughS12nSubscriptionFreeTrial) {
      return <S12nSubscriptionVPropFreeTrial onSdp={onSdp} s12n={s12n} />;
    } else if (canEnrollThroughS12nSubscription) {
      return <S12nSubscriptionVProp onSdp={onSdp} />;
    } else {
      return null;
    }
  };

  switch (activeModal) {
    case MODAL_TYPES.ENROLL:
      return (
        <div className="rc-SubscriptionEnrollModal" data-testid="subscription-enroll-modal">
          <CSSTransitionGroup transitionName="fade" transitionEnter={false} transitionLeaveTimeout={300}>
            <Modal
              trackingName="subscription_enroll_modal"
              key="S12nSubscriptionEnrollModal"
              modalName={_t('Join this Specialization')}
              handleClose={handleClose}
              className="subscription-enroll-modal"
            >
              <div className="enroll-modal-container" ref={visibilityTrackingRef}>
                {isFromS12nSelection && (
                  <div className="m-t-1s">
                    <strong data-unit="step2">{_t('Step 2 of 2')}</strong>
                  </div>
                )}
                {didSkipMixAndMatchModal && (
                  <p data-testid="mix-and-match-s12n-context">
                    <MixAndMatchS12nContextMessage />
                  </p>
                )}
                {renderHeader()}
                <PromotionApplicableCheckoutMessage
                  course={course}
                  s12n={s12n}
                  isFromS12nSelection={isFromS12nSelection}
                />
                {renderBody()}
                <S12nSubscriptionEnrollButton
                  isFromS12nSelection={isFromS12nSelection}
                  onClick={onClickSubscribeCta}
                  enrolling={enrolling}
                />
                {!onSdp && <S12nSubscriptionFooter />}
              </div>
            </Modal>
          </CSSTransitionGroup>
        </div>
      );
    case MODAL_TYPES.ERROR:
      return (
        <EnrollErrorModal
          error={error}
          onClose={handleClose}
          isFinancialAid={false}
          isFreeEnroll={false}
          courseId={course?.id}
          s12nId={s12n?.id}
          isSpecialization={isSpecialization}
        />
      );
    default:
      return null;
  }
};

export default S12nSubscriptionEnrollModal;
